import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { DRILL_LEVERS, cellCenter, drillCell, drillTurnsRight, drillsAsk, strutGolem, strutsLoose, wallForce, type BattleState, type CartsRules, type DrillsRules, type StrutsRules } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** A strut: a timber prop under a beam. */
const STRUT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 3.5h13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><path d="M4 4l2.2 10M12 4l-2.2 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M3 14.5h10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" opacity="0.7"/></svg>';
/** A house cut in the rock, its door lit. */
const HOUSE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 14.5V7l6-4.5L14 7v7.5z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><rect x="6.5" y="9.5" width="3" height="5" rx="0.6" fill="currentColor"/></svg>';
/** The stair groaning: steps with a crack. */
const STAIR = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 14.5h4v-4h4v-4h5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M9 1.5l-1.5 3 2 1-1.5 3" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>';
/** A golem's head and shoulders. */
const GOLEM = '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="5" y="1.8" width="6" height="5" rx="1.2" fill="none" stroke="currentColor" stroke-width="1.3"/><circle cx="8" cy="4.3" r="1.1" fill="currentColor"/><path d="M2 14.5V10c0-1.2 1-2 2.2-2h7.6c1.2 0 2.2.8 2.2 2v4.5" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>';
/** A mine cart on its rail. */
const CART = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 5h12l-1.5 6h-9z" fill="currentColor"/><circle cx="5" cy="12.6" r="1.4" fill="currentColor"/><circle cx="11" cy="12.6" r="1.4" fill="currentColor"/><path d="M1 14.8h14" stroke="currentColor" stroke-width="1" opacity="0.7"/></svg>';
/** A crossing shut by a shell. */
const SHELL = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 11c0-4 2.7-7 6-7s6 3 6 7z" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M8 4v7M4.6 5.6L6 11M11.4 5.6L10 11" stroke="currentColor" stroke-width="1"/><path d="M1 13.5h14" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
/** Ice cracking. */
const CRACK = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5l-1 4 3 1.5-2.5 3 1 4.5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M6.8 5.5L3 7M10 7l3.5-1" stroke="currentColor" stroke-width="1" stroke-linecap="round"/></svg>';
/** The mill's sails. */
const MILL = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="1.5" fill="currentColor"/><path d="M8 6.5V1.5h2.5zM9.5 8h5v2.5zM8 9.5v5H5.5zM6.5 8h-5V5.5z" fill="currentColor"/></svg>';
/** A wall of ice with the geode. */
const WALL = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 2.5h12v11H2z" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M8 5.5l2.4 2.5L8 10.5 5.6 8z" fill="currentColor"/><path d="M2 6h3M11 10h3M5 13.5V11" stroke="currentColor" stroke-width="1" opacity="0.7"/></svg>';
/** A belt crossed over two pulleys. */
const BELT = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="4" cy="4" r="2.2" fill="none" stroke="currentColor" stroke-width="1.3"/><circle cx="12" cy="12" r="2.2" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M5.8 2.6L13.6 10.8M2.4 5.4l7.8 8.2" stroke="currentColor" stroke-width="1.2"/></svg>';
/** La Barrena's auger. */
const AUGER = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5L5 6h6zM5 6l6 2.5M11 8.5L5 11M5 11l6 2M8 15l-2.5-2h5z" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>';

/** Seconds to a tick, at least 1. */
const secs = (s: BattleState, at: number): number => Math.max(1, Math.ceil((at - s.tick) / TICK_HZ));

type Mark = { el: HTMLButtonElement; label: HTMLElement; state: HTMLElement; slot: number; point: THREE.Vector3 };

/**
 * The eyes on screen (docs/sendas/artifice.md, chapter 4), inside a path's own plate (game/senda-hud.ts) and over the
 * board; a phone's plate kept under about a quarter of the screen:
 *
 * - the night of the promise (`struts`, the memory): the four struts as chips (loose, a golem on it, wedged) with
 *   Bruni's wedging; the houses lost (of five); the stair's next groan and how much it would take (each loose strut);
 *   the golems (going, holding, down and lit again in so many seconds). Over the board a mark on each strut: a tap sends
 *   a golem.
 * - the carts (`carts`): the crossings opened (of four); the next crossing's cart (ready: work its brake; running;
 *   built again in so many seconds); the ice cracking. Over the board a mark on each brake (ready or how long).
 * - the drills (`drills`, the test): what the drills in ask of the mill (of 3; red over it: it jams); each wall's cut
 *   («Norte 60 %», down); the belts I and II (as they started or turned round); La Barrena's state (out of its heap,
 *   for a drill, for the hero, its slash and its spin). Over the board a mark on each drill (its force, «adentro» or
 *   «afuera», turning right or backwards, stopped) and on each lever.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class OjosHud {
  readonly panel: HTMLElement;
  private kind: 'struts' | 'carts' | 'drills';
  private v = new THREE.Vector3();
  private key = '';
  private rowA = h('div.bh-row');
  private textA = h('span.al-wave');
  private rowB = h('div.bh-row.al-waverow');
  private textB = h('span.al-wave');
  private rowC = h('div.bh-row.al-waverow');
  private textC = h('span.al-wave');
  private rowD = h('div.bh-row.al-waverow');
  private textD = h('span.al-wave');
  private bar = h('i.cb-bar');
  private chips: HTMLElement[] = [];
  private pips: HTMLElement[] = [];
  private marks: Mark[] = [];

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const sr = s.rules?.senda;
    this.kind = sr?.drills ? 'drills' : sr?.carts ? 'carts' : 'struts';
    const rows: HTMLElement[] = [];
    if (sr?.struts) {
      const r = sr.struts;
      this.chips = r.struts.map((_, i) => h('b.oj-chip', {}, String(i + 1)));
      this.rowA.append(h('span.bh-ico', { html: STRUT }), h('span.bh-label', {}, t('senda.ojos.struts')), h('span.oj-chips', {}, ...this.chips), this.textA, h('span.cb-track', {}, this.bar));
      this.pips = Array.from({ length: r.houses }, () => h('i'));
      this.rowB.append(h('span.bh-ico', { html: HOUSE }), h('span.bh-label', {}, t('senda.ojos.houses')), h('span.al-pips.cb-pips.oj-houses', { title: t('senda.ojos.housesHint') }, ...this.pips));
      this.rowC.append(h('span.bh-ico', { html: STAIR }), this.textC);
      this.rowD.append(h('span.bh-ico', { html: GOLEM }), this.textD);
      rows.push(this.rowA, this.rowB, this.rowC, this.rowD);
      this.strutMarks(s, r);
    } else if (sr?.carts) {
      const r = sr.carts;
      this.chips = r.rails.map((_, i) => h('b.oj-chip', {}, String(i + 1)));
      this.rowA.append(h('span.bh-ico', { html: SHELL }), h('span.bh-label', {}, t('senda.ojos.crossings')), h('span.oj-chips', {}, ...this.chips));
      this.rowB.append(h('span.bh-ico', { html: CART }), this.textB);
      this.rowC.append(h('span.bh-ico', { html: CRACK }), this.textC);
      rows.push(this.rowA, this.rowB, this.rowC);
      this.brakeMarks(s, r);
    } else if (sr?.drills) {
      const r = sr.drills;
      this.pips = Array.from({ length: r.mill }, () => h('i'));
      this.rowA.append(h('span.bh-ico', { html: MILL }), h('span.bh-label', {}, t('senda.ojos.mill')), h('span.al-pips.cb-pips.oj-force', { title: t('senda.ojos.millHint') }, ...this.pips), this.textA);
      this.rowB.append(h('span.bh-ico', { html: WALL }), this.textB);
      this.rowC.append(h('span.bh-ico', { html: BELT }), this.textC);
      this.rowD.append(h('span.bh-ico', { html: AUGER }), h('span.bh-label', {}, t('senda.ojos.boss')), this.textD);
      rows.push(this.rowA, this.rowB, this.rowC, this.rowD);
      this.drillMarks(s, r);
    }
    this.panel = h(`div.bh.tl-panel.cb-panel.oj-panel.oj-${this.kind}`, {}, ...rows);
  }

  private addMark(slot: number, kind: string, test: string, hint: string, x: number, y: number, name = ''): Mark {
    const label = h('b.tl-teeth');
    const state = h('small.al-state.tl-state');
    const el = h(
      `button.al-stone.tl-slot.cb-slot.oj-slot.${kind}`,
      { type: 'button', 'data-test': test, 'aria-label': t(hint), title: t(hint), onpointerup: (e: Event) => (e.stopPropagation(), this.mark(slot)) },
      h('span.al-ring'),
      label,
      name ? h('b.mn-name.tl-name', {}, name) : '',
      state,
    ) as HTMLButtonElement;
    this.board.append(el);
    const m = { el, label, state, slot, point: simToWorld(x, y, new THREE.Vector3(), 0.3) };
    this.marks.push(m);
    return m;
  }

  /** A mark on each strut. */
  private strutMarks(s: BattleState, r: StrutsRules): void {
    r.struts.forEach((c, i) => {
      const p = cellCenter(s.map.radius, c);
      const m = this.addMark(i, 'strut', `strut-${i}`, 'senda.ojos.strutHint', p.x, p.y - 250);
      m.label.textContent = String(i + 1);
    });
  }

  /** A mark on each brake. */
  private brakeMarks(s: BattleState, r: CartsRules): void {
    r.rails.forEach((rl, i) => {
      const p = cellCenter(s.map.radius, rl.brake);
      const m = this.addMark(i, 'brake', `cart-brake-${i}`, 'senda.ojos.brakeHint', p.x, p.y, t('senda.ojos.brake'));
      m.label.textContent = String(i + 1);
    });
  }

  /** A mark on each drill (its clutch) and on each lever. */
  private drillMarks(s: BattleState, r: DrillsRules): void {
    r.drills.forEach((d, i) => {
      const p = cellCenter(s.map.radius, drillCell(r, i));
      const m = this.addMark(i, 'drill', `drill-${i}`, 'senda.ojos.drillHint', (p.x + d.at.x) >> 1, (p.y + d.at.y) >> 1);
      m.label.textContent = String(d.force);
    });
    // (The two levers stand on cells side by side: their marks pulled apart, west and east, or their words overlap.)
    r.levers.forEach((c, b) => {
      const p = cellCenter(s.map.radius, c);
      const m = this.addMark(DRILL_LEVERS + b, 'lever', `drill-lever-${b}`, 'senda.ojos.leverHint', p.x + (b === 0 ? -450 : 450), p.y + (b === 0 ? -250 : 250));
      m.label.textContent = b === 0 ? 'I' : 'II';
    });
  }

  private updateStruts(s: BattleState): void {
    const r = s.rules?.senda?.struts;
    const w = s.senda?.struts;
    if (!r || !w) return;
    this.chips.forEach((c, i) => {
      c.classList.toggle('done', w.st[i] === 2);
      c.classList.toggle('held', w.st[i] === 1);
      c.classList.toggle('next', w.point === i && w.st[i] !== 2);
    });
    const job = w.job >= 0 && w.job < 10 ? w.job : -1;
    const k = job >= 0 ? Math.floor(((w.prog[job] ?? 0) * 100) / r.caulk) : 0;
    const done = w.st.filter((x) => x === 2).length;
    const a = w.end >= 0 ? t('senda.ojos.allWedged') : job >= 0 ? t('senda.ojos.wedging', { n: job + 1, k }) : w.job >= 10 ? t('senda.ojos.lighting') : t('senda.ojos.wedged', { n: done, of: r.struts.length });
    if (this.textA.textContent !== a) this.textA.textContent = a;
    setStyle(this.bar, 'width', `${job >= 0 ? k : w.end >= 0 ? 100 : 0}%`);
    this.pips.forEach((p, i) => p.classList.toggle('on', i < w.leaks));
    this.rowB.classList.toggle('warn', w.leaks >= r.houses - 2);
    const loose = strutsLoose(w);
    const c = w.end >= 0 ? '' : w.groanAt >= 0 ? t('senda.ojos.groanNow', { s: secs(s, w.groanAt), n: loose, k: Math.round((loose * r.groan.bp) / 100) }) : loose ? t('senda.ojos.groanIn', { s: Math.max(1, Math.ceil((w.groan - s.waveT) / TICK_HZ)), n: loose }) : t('senda.ojos.groanNone');
    if (this.textC.textContent !== c) this.textC.textContent = c;
    this.rowC.hidden = !c;
    this.rowC.classList.toggle('warn', w.groanAt >= 0);
    this.rowC.classList.toggle('good', !loose && w.end < 0);
    const g = w.golems.map((x, i) => {
      if (x.mode === 4) return t('senda.ojos.golemDown', { n: i + 1 });
      if (x.mode === 3) return t('senda.ojos.golemHolds', { n: i + 1, s: x.strut + 1 });
      if (x.mode === 2) return t('senda.ojos.golemSets', { n: i + 1, s: x.strut + 1 });
      if (x.mode === 1) return t('senda.ojos.golemGoes', { n: i + 1, s: x.strut + 1 });
      return t('senda.ojos.golemFree', { n: i + 1 });
    });
    const d = w.end >= 0 ? '' : g.join(' · ');
    if (this.textD.textContent !== d) this.textD.textContent = d;
    this.rowD.hidden = !d;
    this.rowD.classList.toggle('warn', w.golems.some((x) => x.mode === 4));
    const key = `${w.st.join(',')}|${w.golems.map((x) => `${x.mode}:${x.strut}`).join(',')}|${w.end}`;
    if (key !== this.key) {
      this.key = key;
      for (const m of this.marks) {
        const i = m.slot;
        const going = strutGolem(w, i) >= 0 && w.st[i] === 0;
        const st = w.st[i] === 2 ? t('senda.ojos.wedgedMark') : w.st[i] === 1 ? t('senda.ojos.heldMark') : going ? t('senda.ojos.goingMark') : t('senda.ojos.looseMark');
        if (m.state.textContent !== st) {
          m.state.textContent = st;
          remeasureMark(m.el);
        }
        m.el.classList.toggle('full', w.st[i] === 2);
        m.el.classList.toggle('held', w.st[i] === 1 || going);
        m.el.classList.toggle('ready', w.st[i] === 0 && !going && w.golems.some((x) => x.mode === 0));
      }
    }
    for (const m of this.marks) {
      m.el.classList.toggle('next', w.point === m.slot && w.st[m.slot] !== 2);
      m.el.disabled = s.result !== 'running' || w.st[m.slot] !== 0;
    }
  }

  private updateCarts(s: BattleState): void {
    const r = s.rules?.senda?.carts;
    const w = s.senda?.carts;
    if (!r || !w) return;
    this.chips.forEach((c, i) => {
      c.classList.toggle('done', i < w.open);
      c.classList.toggle('next', i === w.open);
    });
    const i = w.open;
    const cart = w.carts[i];
    const b = w.end >= 0 ? t('senda.ojos.hall') : i >= r.rails.length ? t('senda.ojos.toDoor') : !cart ? '' : cart.mode === 1 ? t('senda.ojos.cartRuns') : cart.mode === 2 ? t('senda.ojos.cartBuilt', { s: secs(s, cart.t) }) : w.job === i ? t('senda.ojos.braking') : t('senda.ojos.cartReady', { n: i + 1 });
    if (this.textB.textContent !== b) this.textB.textContent = b;
    this.rowB.classList.toggle('good', !!cart && cart.mode === 0 && i < r.rails.length);
    this.rowB.classList.toggle('warn', !!cart && cart.mode === 2);
    const on = w.spots.length > 0;
    const c = w.end >= 0 ? '' : on ? t('senda.ojos.crackNow') : t('senda.ojos.crackIn', { s: Math.max(1, Math.ceil((w.crack - s.waveT) / TICK_HZ)) });
    if (this.textC.textContent !== c) this.textC.textContent = c;
    this.rowC.hidden = !c;
    this.rowC.classList.toggle('warn', on);
    for (const m of this.marks) {
      const k = w.carts[m.slot];
      const st = !k ? '' : k.mode === 0 ? t('senda.ojos.readyMark') : k.mode === 1 ? t('senda.ojos.runsMark') : t('senda.ojos.inMark', { s: secs(s, k.t) });
      if (m.state.textContent !== st) {
        m.state.textContent = st;
        remeasureMark(m.el);
      }
      m.el.classList.toggle('ready', !!k && k.mode === 0 && m.slot === w.open);
      m.el.classList.toggle('full', m.slot < w.open);
      const sent = w.job === m.slot;
      m.el.classList.toggle('sent', sent);
      m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, r.brake)).toFixed(2) : '0');
      m.el.disabled = s.result !== 'running';
    }
  }

  private updateDrills(s: BattleState): void {
    const r = s.rules?.senda?.drills;
    const w = s.senda?.drills;
    if (!r || !w) return;
    const ask = drillsAsk(r, w);
    this.pips.forEach((p, i) => p.classList.toggle('on', i < ask));
    const a = ask > r.mill ? t('senda.ojos.jam') : t('senda.ojos.ask', { n: ask, of: r.mill });
    if (this.textA.textContent !== a) this.textA.textContent = a;
    this.rowA.classList.toggle('warn', ask >= r.mill);
    const wall = (k: number): string => {
      const name = t(k === 0 ? 'senda.ojos.north' : 'senda.ojos.south');
      if ((w.down[k] as number) >= 0) return t('senda.ojos.wallDown', { name });
      const f = wallForce(s, r, w, k);
      return f >= r.mill ? t('senda.ojos.wallCut', { name, n: Math.floor(((w.cut[k] as number) * 100) / r.cut) }) : t('senda.ojos.wallForce', { name, n: f, of: r.mill });
    };
    const bb = `${wall(0)} · ${wall(1)}`;
    if (this.textB.textContent !== bb) this.textB.textContent = bb;
    this.rowB.classList.toggle('good', [0, 1].some((k) => (w.down[k] as number) < 0 && wallForce(s, r, w, k) >= r.mill));
    const belt = (b: number): string => t(w.belts[b] ? 'senda.ojos.beltTurned' : 'senda.ojos.beltAsIs', { n: t(b === 0 ? 'senda.ojos.beltI' : 'senda.ojos.beltII') });
    const c = `${belt(0)} · ${belt(1)}`;
    if (this.textC.textContent !== c) this.textC.textContent = c;
    const bz = w.bz;
    const hero = s.heroes[0];
    const e = bz.id ? s.enemies.find((x) => x.id === bz.id) : undefined;
    const near = !!hero && !!e && Math.abs(hero.x - e.x) + Math.abs(hero.y - e.y) < r.boss.chase;
    const d =
      bz.mode === 3
        ? t('senda.ojos.bossDown')
        : bz.mode === 0
          ? t('senda.ojos.heap')
          : bz.spinAt >= 0
            ? t('senda.ojos.spin', { s: secs(s, bz.spinAt) })
            : bz.wind >= 0
              ? t('senda.ojos.slash', { s: secs(s, bz.wind) })
              : s.tick < bz.again || near || !w.inn.some(Boolean)
                ? t('senda.ojos.atYou')
                : t('senda.ojos.toDrill');
    if (this.textD.textContent !== d) this.textD.textContent = d;
    this.rowD.classList.toggle('warn', bz.mode !== 3 && (bz.spinAt >= 0 || bz.wind >= 0));
    for (const m of this.marks) {
      let st: string;
      if (m.slot < DRILL_LEVERS) {
        const i = m.slot;
        const stalled = s.tick < (w.stall[i] as number);
        const right = drillTurnsRight(r, w, i);
        st = !w.inn[i] ? t('senda.ojos.outMark') : stalled ? t('senda.ojos.stallMark') : right ? t('senda.ojos.rightMark') : t('senda.ojos.backMark');
        m.el.classList.toggle('full', w.inn[i] === true && right && !stalled);
        m.el.classList.toggle('wrong', w.inn[i] === true && !right);
        m.el.classList.toggle('ready', false);
      } else {
        const b = m.slot - DRILL_LEVERS;
        st = w.belts[b] ? t('senda.ojos.turnedMark') : t('senda.ojos.asIsMark');
      }
      if (m.state.textContent !== st) {
        m.state.textContent = st;
        remeasureMark(m.el);
      }
      const sent = w.job === m.slot;
      m.el.classList.toggle('sent', sent);
      const need = m.slot < DRILL_LEVERS ? r.clutch : r.lever;
      m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, need)).toFixed(2) : '0');
      m.el.classList.toggle('gone', (w.down[0] as number) >= 0 && (w.down[1] as number) >= 0);
      m.el.disabled = s.result !== 'running';
    }
  }

  update(s: BattleState): void {
    if (this.kind === 'struts') this.updateStruts(s);
    else if (this.kind === 'carts') this.updateCarts(s);
    else this.updateDrills(s);
  }

  /** Every frame: the marks over their places. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
  }
}
